import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Upload, Check, X, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [uploads, setUploads] = useState([]); // {name, status, error}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/upload/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setLoadError(e?.response?.data?.detail || "This upload link is invalid or expired."));
  }, [token]);

  const uploadOne = useCallback(async (file) => {
    const idx = uploads.length;
    setUploads((prev) => [...prev, { name: file.name, status: "uploading" }]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/public/upload/${token}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploads((prev) => prev.map((u, i) => (i === idx ? { ...u, status: "done" } : u)));
    } catch (e) {
      setUploads((prev) =>
        prev.map((u, i) =>
          i === idx ? { ...u, status: "error", error: e?.response?.data?.detail || "Upload failed" } : u,
        ),
      );
    }
  }, [uploads.length, token]);

  const onFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setBusy(true);
    for (const f of Array.from(fileList)) {
      // sequential — keeps the pod happy and the list ordered
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(f);
    }
    setBusy(false);
  };

  if (loadError) {
    return (
      <div className="min-h-screen grid place-items-center bg-white px-6">
        <div className="max-w-md text-center">
          <Logo />
          <h1 className="mt-8 text-3xl font-semibold text-[#1F2937]">Link unavailable</h1>
          <p className="mt-3 text-[15px] text-slate-500">{loadError}</p>
          <p className="mt-6 text-[13px] text-slate-400">
            Please contact your loan officer to request a new upload link.
          </p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-6 py-5">
        <div className="mx-auto max-w-3xl">
          <Logo />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12" data-testid="public-upload-page">
        <div className="small-caps text-slate-500">Secure document upload</div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold text-[#1F2937]">
          {info.business_name || info.borrower_name || "Your file"}
        </h1>
        <p className="mt-2 small-caps text-slate-500">
          File {info.loan_number} · {info.product_name || "Working Capital"}
        </p>

        <div className="mt-10 border-2 border-dashed border-slate-300 rounded-lg p-10 text-center hover:border-[#1F2937] transition-colors">
          <input
            type="file"
            id="file-input"
            multiple
            className="hidden"
            data-testid="public-upload-input"
            onChange={(e) => onFiles(e.target.files)}
          />
          <label
            htmlFor="file-input"
            className="cursor-pointer inline-flex flex-col items-center gap-3"
          >
            <Upload className="h-8 w-8 text-slate-400" />
            <div className="font-semibold text-[17px] text-[#1F2937]">
              Choose files to upload
            </div>
            <div className="small-caps text-slate-500">
              PDF, images, or docs · we&#39;ll notify your loan officer
            </div>
          </label>
        </div>

        {info.required_documents?.length > 0 && (
          <div className="mt-10">
            <div className="small-caps text-slate-500">Documents your loan officer is expecting</div>
            <ul className="mt-3 space-y-1.5 text-[14px] text-[#1F2937]">
              {info.required_documents.map((d, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <FileText className="h-3.5 w-3.5 text-slate-400" /> {d}
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploads.length > 0 && (
          <div className="mt-10">
            <div className="small-caps text-slate-500">
              {busy ? "Uploading…" : `${uploads.filter((u) => u.status === "done").length} of ${uploads.length} sent`}
            </div>
            <ul className="mt-3 space-y-2" data-testid="public-upload-list">
              {uploads.map((u, i) => (
                <li key={i} className="flex items-center justify-between gap-4 py-2 border-b border-slate-200">
                  <div className="truncate text-[14px] text-[#1F2937]">{u.name}</div>
                  <div className="shrink-0">
                    {u.status === "uploading" && <span className="small-caps text-slate-400">sending…</span>}
                    {u.status === "done" && (
                      <span className="inline-flex items-center gap-1 small-caps text-emerald-700">
                        <Check className="h-3.5 w-3.5" /> Sent
                      </span>
                    )}
                    {u.status === "error" && (
                      <span className="inline-flex items-center gap-1 small-caps text-red-700" title={u.error}>
                        <X className="h-3.5 w-3.5" /> Failed
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-16 text-[12px] text-slate-400">
          This link expires in 14 days. Keep it private — anyone with the link can upload to your file.
        </p>
      </main>
    </div>
  );
}
